import { css } from '@emotion/react';
import { Route, useHistory, useParams, useLocation, Switch, Redirect } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { color, shadow, spacing, Typography } from '@uniquegood/realworld-studio-design';

import { useRequest, useRestrictProjectRoute } from '@/hooks';
import useMission from '@/hooks/useMission';
import LoadingSpinner from '@/components/LoadingSpinner';
import { onMobile } from '@/styles/responsive';
import { MissionListBar } from '@/pages/missions/components/missionList';
import { EditMissionPage, EditQuestPage } from '@/pages/missions/components/editPage';
import { MissionFlowView } from '@/pages/missions/components/flow';
import { FlexContentCenter, FlexGrow } from '@/styles';
import { TOP_BAR_HEIGHT } from '@/components/TopBar/Topbar';
import { MissionEditMode } from '@/pages/missions/types';
import MissionOnboarding from './components/MissionOnboarding';
import { MissionListBarMobileHeight } from './components/missionList/MissionListBar';

export default function Missions() {
  const history = useHistory();
  const { pathname } = useLocation();
  const { appId, projectId, scenarioId, missionId, questId } = useParams<AppParam>();
  const [missionEditMode, setMissionEditMode] = useState<MissionEditMode>('missionCard');

  const scenariosEndpoint = `/apps/${appId}/projects/${projectId}/scenarios`;

  const scenariosPath = `/apps/${appId}/projects/${projectId}/scenarios`;
  const missionsPath = `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions`;

  const { data: scenarios } = useRequest<Scenario[]>(scenariosEndpoint);
  const { missions, mission } = useMission();

  const redirectTo = useRestrictProjectRoute();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [missionId, questId]);

  useEffect(() => {
    if (pathname === scenariosPath && scenarios && scenarios.length > 0) {
      history.replace(`${scenariosPath}/${scenarios[0].id}`);
      return;
    }

    if (!pathname.startsWith(missionsPath) && missions?.[0]?.id) {
      history.replace(`${scenariosPath}/${scenarioId}/missions/${missions[0].id}`);
      return;
    }

    if (pathname === missionsPath && missions && missions.length > 0) {
      history.replace(`${scenariosPath}/${scenarioId}/missions/${missions[0].id}`);
      return;
    }

    // URL에 missionId가 있지만 해당 미션이 존재하지 않는 경우, 첫 번째 미션으로 리다이렉트
    if (missionId && missions && missions.length > 0 && !mission) {
      history.replace(`${scenariosPath}/${scenarioId}/missions/${missions[0].id}`);
    }
  }, [pathname, scenarios, missions, missionId, mission]);

  if (!missions || redirectTo === undefined)
    return (
      <div css={FlexGrow}>
        <LoadingSpinner />
      </div>
    );

  if (typeof redirectTo === 'string') return <Redirect to={redirectTo} />;

  return (
    <>
      <div css={[MissionPageLayout, missionEditMode === 'flow' && FlowModeLayout]}>
        {missionEditMode === 'flow' ? (
          <MissionFlowView onSwitchToCardMode={() => setMissionEditMode('missionCard')} />
        ) : (
          <>
            <MissionListBar
              missionEditMode={missionEditMode}
              onChangeMissionEditMode={setMissionEditMode}
            />
            <Switch>
              <Route
                path="/apps/:appId/projects/:projectId/scenarios/:scenarioId/missions/:missionId/quests/:questId"
                component={EditQuestPage}
              />
              <Route
                path="/apps/:appId/projects/:projectId/scenarios/:scenarioId/missions/:missionId"
                component={EditMissionPage}
              />
              <Route
                path={[
                  '/apps/:appId/projects/:projectId/scenarios/:scenarioId',
                  '/apps/:appId/projects/:projectId/scenarios'
                ]}
                render={() => (
                  <>
                    {EmptyStateMarkup}
                    <MissionOnboarding />
                    {/* <QuestOnboarding /> */}
                  </>
                )}
              />
            </Switch>
          </>
        )}
      </div>
    </>
  );
}

export const MissionContainerMarginLeft = css`
  margin-left: 240px;
`;

const MissionPageLayout = css`
  display: flex;
  flex: 1 0;

  ${onMobile} {
    display: block;
  }

  > *:not(nav) {
    flex: 1 1;

    ${MissionContainerMarginLeft}

    ${onMobile} {
      margin-left: initial;
      height: initial;
    }
  }
`;

const FlowModeLayout = css`
  > *:not(nav) {
    margin-left: 0;
  }
`;

const EmptyStateStyle = css`
  background-color: ${color.white};
  ${shadow.card};

  padding: ${spacing.padding.large};

  text-align: center;

  ${onMobile} {
    && {
      flex: initial;
      height: calc(100vh - ${MissionListBarMobileHeight} - ${TOP_BAR_HEIGHT});
    }
  }
`;

const EmptyStateEmoji = css`
  ::before {
    content: '👈';
    margin-right: 0.25em;
    ${onMobile} {
      content: '☝️';
    }
  }
`;

const EmptyStateMarkup = (
  <div css={[EmptyStateStyle, FlexContentCenter]}>
    <Typography as="p" type="displayMedium" textColor="disabled" cssStyle={EmptyStateEmoji}>
      새 미션을 만들고 게임 제작을 시작해보세요
    </Typography>
  </div>
);
